import type { KeyboardEvent } from 'react';
import type { Row, Table } from '@/lib/tanstack-table';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { OVERRIDDEN_VALUE_CLASS } from '../../forecast/details/inline-overrides/overrideMarking';
import { isQuantityEditable, quantityDiff } from '../utils/lineQuantity';
import {
  useApplyPriceBreak,
  useOverrideOption,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { useIsPriceBreakNudgeDismissed } from 'src/api/rpc/hooks/procurement/dismissedPriceBreaks';
import { isPriceBreakOfferChanged } from 'src/api/rpc/hooks/procurement/priceBreakOfferChanged';
import { toast } from 'src/components/ui/toast';
import { getCartLineTableMeta } from './cartLineTableMeta';
import { buildQuantityOverride } from './quantityOverride';
import { useCalculatedDescriptionId } from './calculatedDescription';
import { ForwardBuyLineIndicator } from './ForwardBuyLineIndicator';
import { PriceBreakNudgeChip } from './PriceBreakNudgeChip';
import { resolvePriceBreakNudge } from './priceBreakNudge';
import { useLineQuantityDraft } from './useLineQuantityDraft';
import { useLineBulkEditTargets } from './lineBulkEditContext';
import { settledMutationCounts } from '../utils/settledMutationCounts';
import {
  handleQuantityKeyDown,
  quantityDraft,
  selectOnFirstClick,
} from './quantityColumnKeyboard';

export function QuantityCell({
  row,
  table,
}: {
  row: Row<AssignmentLine>;
  table: Table<AssignmentLine>;
}) {
  const meta = getCartLineTableMeta(table);
  const editTargets = useLineBulkEditTargets();
  const override = useOverrideOption();
  const applyPriceBreak = useApplyPriceBreak();
  const line = row.original;
  const option = line.topOption;
  const nudgeDismissed = useIsPriceBreakNudgeDismissed(option?.optionId);
  const quantity =
    option?.buyQuantity ?? option?.transferQuantity ?? line.neededQuantity;
  const {
    draft,
    shownDraft,
    isSaving,
    setDraft: updateDraft,
    track: tracked,
  } = useLineQuantityDraft(
    line.suggestionId,
    override.isPending || applyPriceBreak.isPending,
  );
  const calculatedDescriptionId = useCalculatedDescriptionId();
  const displayedValue = shownDraft ?? String(quantity);
  const editable = isQuantityEditable(line);
  const marking = cn(quantityDiff(line).differs && OVERRIDDEN_VALUE_CLASS);

  const reset = () => updateDraft(null);
  // Both ways the buyer changes this quantity, typing in the input and taking
  // the price-break nudge below it, fail the same way.
  const failSave = () => {
    reset();
    toast.error(
      meta.gt('Could not update quantity. Try again.', {
        context: 'replenishment inline quantity update error',
      }),
    );
  };
  const lineById = (suggestionId: string) =>
    table
      .getRowModel()
      .rows.find((row) => row.original.suggestionId === suggestionId)?.original;

  // The overrides `value` saves: one per bulk-edit target it changes.
  const planSaves = (value: string) => {
    const saves: Array<
      Extract<ReturnType<typeof buildQuantityOverride>, { kind: 'save' }>
    > = [];
    let sawMissingContext = false;
    for (const suggestionId of editTargets(line.suggestionId)) {
      const targetLine =
        suggestionId === line.suggestionId ? line : lineById(suggestionId);
      if (!targetLine) continue;
      const result = buildQuantityOverride({
        line: targetLine,
        value,
        supplierId: meta.supplierId,
        sourceWarehouseId: meta.sourceWarehouseId,
      });
      if (result.kind === 'missing-context') {
        sawMissingContext = true;
        continue;
      }
      if (result.kind === 'save') saves.push(result);
    }
    return { saves, sawMissingContext };
  };

  const commit = (value: string) => {
    const { saves, sawMissingContext } = planSaves(value);
    if (saves.length === 0) {
      reset();
      if (sawMissingContext) {
        toast.error(
          meta.gt('Could not update quantity for this plan.', {
            context: 'replenishment inline quantity update error',
          }),
        );
      }
      return;
    }

    // Settled through the promise, not mutate's per-call callbacks: those are
    // dropped once this cell unmounts, and a virtualized table unmounts a row
    // that scrolls away mid-save, which would strand the shared draft and
    // swallow a failure.
    const bulkTargetIds = saves.map((save) => save.input.suggestionId);
    void tracked(
      settledMutationCounts(saves, (save) =>
        override.mutateAsync(save.input),
      ).then(({ failed }) => {
        if (failed > 0) failSave();
        else reset();
      }),
      bulkTargetIds,
    );
  };
  // The nudge names the line and its option only. The server picks the
  // quantity (the break rounded to the vendor's MOQ and order multiple) and the
  // price at that quantity, so neither can arrive stale from this row. A
  // changed vendor offer is not a failed save: the hook already told the buyer
  // and dismissed this option's nudge.
  const takeNudge = (optionId: string) => {
    void tracked(
      applyPriceBreak.mutateAsync({
        suggestionId: line.suggestionId,
        optionId,
      }),
    ).then(reset, (error: unknown) =>
      isPriceBreakOfferChanged(error) ? reset() : failSave(),
    );
  };
  const save = () => {
    if (draft == null) return;
    commit(draft);
  };
  // A bulk edit locks every line it saves.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) =>
    handleQuantityKeyDown(event, {
      revert: reset,
      locking: () =>
        new Set(
          draft == null
            ? []
            : planSaves(draft).saves.map(
                (planned) => planned.input.suggestionId,
              ),
        ),
    });

  if (!editable) {
    return (
      <span className={cn('text-xs tabular-nums', marking)}>
        {quantity.toLocaleString()}
      </span>
    );
  }

  // The provenance belongs to the buy leg, so it is measured against the saved
  // buy quantity rather than whatever half-typed draft is in the input.
  const priceBreak = option?.priceBreak ?? null;
  const nudge = resolvePriceBreakNudge({
    priceBreak,
    quantity: option?.buyQuantity,
  });

  return (
    <div className="flex shrink-0 flex-col items-end gap-0.5">
      {/* Text, not number: arrows move between lines instead of stepping the
          value, and a mouse wheel over the focused input cannot change it. */}
      <Input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={displayedValue}
        disabled={isSaving}
        aria-label={meta.gt('Order quantity', {
          context: 'replenishment inline quantity input label',
        })}
        aria-describedby={calculatedDescriptionId}
        // LINE_QUANTITY_ATTRIBUTE: how Enter finds the next line's quantity.
        data-line-quantity={line.suggestionId}
        onChange={(event) => {
          const next = quantityDraft(event.target.value);
          if (next !== null) updateDraft(next);
        }}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={save}
        onKeyDown={handleKeyDown}
        onMouseDown={selectOnFirstClick}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          // The sticky table header would otherwise cover a line reached by
          // moving up.
          'h-7 w-20 scroll-mt-8 px-2 text-right text-xs tabular-nums group-data-[density=compact]/table:h-6',
          marking,
        )}
      />
      {option != null &&
        priceBreak != null &&
        nudge != null &&
        !nudgeDismissed && (
          <PriceBreakNudgeChip
            nudge={nudge}
            priceBreak={priceBreak}
            onApply={() => takeNudge(option.optionId)}
            isApplying={isSaving}
            compact
          />
        )}
      {/* Sits under the quantity because it is the quantity that was changed:
          it spells out base + forward buy = total and how far ahead that buys.
          Display only; the cart's "Remove Forward Buy" is the one action. */}
      <ForwardBuyLineIndicator line={line} />
    </div>
  );
}
